import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { api, type Reader, type ReaderLink } from '@/api/client';
import { useCellCommentShellOptional } from '@/features/project-cell-comments/cellCommentShellContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { ContractorCombo } from '@/components/ContractorCombo/ContractorCombo';
import type { ContractorValueItem } from '@/components/ContractorCombo/ContractorCombo';
import { Input } from '@/components/ui/Input/Input';
import { Button } from '@/components/ui/Button/Button';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import type { ContextMenuItem } from '@/components/ui/ContextMenu/ContextMenu';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/useAuth';
import { omitPriceKeys } from '@/lib/omitPriceKeys';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { formatPriceInputDisplay, sortReaderCatalogPrices } from '@/utils/formatNumber';
import {
  ReaderCatalogPricePositionsEditor,
  MAX_READER_CATALOG_PRICE_POSITIONS,
  type ReaderCatalogPriceDraftRow,
} from '@/components/ReaderCatalogPricePositionsEditor/ReaderCatalogPricePositionsEditor';
import {
  patchReaderInTableCache,
  invalidateReaderCachesExceptTable,
} from '@/lib/readersCatalogQueryCache';
import '@/components/ProjectsTable/EditableCell.css';
import { EditableCellPopoverHeader } from '@/components/ProjectsTable/EditableCellPopoverHeader';
import '@/components/CreatePersonModal/CreatePersonModal.css';

const DEMO_SLOTS = 3;
/** Совпадает с лимитом на сервере и в CreatePersonModal */
const MAX_READER_DEMO_LINKS = 3;

type ReaderLinkDraft = { link: string; link_name: string };

function openExternalDemoUrl(raw: string): void {
  const t = raw.trim();
  if (!t) return;
  let u: URL;
  try {
    u = new URL(t.includes('://') ? t : `https://${t}`);
  } catch {
    return;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return;
  window.open(u.href, '_blank', 'noopener,noreferrer');
}

export type ReaderCatalogField = 'studio' | 'legal_entity' | 'reader_price' | 'demo';

export type ReaderCatalogEditableCellProps =
  | { reader: Reader; field: 'demo'; disabled?: boolean }
  | { reader: Reader; field: 'studio' | 'legal_entity' | 'reader_price'; children: ReactNode; disabled?: boolean };

function sortReaderLinks(links: Reader['links']): ReaderLink[] {
  const list = links ?? [];
  return [...list].sort((a, b) => a.display_order - b.display_order);
}

function demoSlotsFromLinks(links: Reader['links']): (ReaderLink | null)[] {
  const sorted = sortReaderLinks(links);
  const out: (ReaderLink | null)[] = [];
  for (let i = 0; i < DEMO_SLOTS; i += 1) {
    out.push(sorted[i] ?? null);
  }
  return out;
}

function fieldTitle(field: ReaderCatalogField): string {
  switch (field) {
    case 'studio':
      return 'Студия';
    case 'legal_entity':
      return 'Юрлицо';
    case 'reader_price':
      return 'Стоимость';
    case 'demo':
      return 'Демо';
    default: {
      const _exhaustive: never = field;
      return _exhaustive;
    }
  }
}

function fieldPopoverWidth(field: ReaderCatalogField): number {
  switch (field) {
    case 'studio':
    case 'legal_entity':
      return 500;
    case 'reader_price':
      return 520;
    case 'demo':
      return 520;
    default: {
      const _exhaustive: never = field;
      return _exhaustive;
    }
  }
}

export function ReaderCatalogEditableCell(props: ReaderCatalogEditableCellProps) {
  const { reader, field, disabled = false } = props;
  const children = props.field === 'demo' ? null : props.children;

  const cellCommentShell = useCellCommentShellOptional();
  const { showSuccess, showError } = useToast();
  const { capabilities } = useAuth();
  const hidePrices = !capabilities.prices;
  const queryClient = useQueryClient();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [error, setError] = useState('');

  const [bindDraft, setBindDraft] = useState<ContractorValueItem | null>(null);
  const [priceRows, setPriceRows] = useState<ReaderCatalogPriceDraftRow[]>([]);
  const [demoRows, setDemoRows] = useState<ReaderLinkDraft[]>([]);
  /** 'plus' — открыто с «+», не перезаполнять demoRows из reader в effect (иначе Strict Mode сотрёт новую строку). */
  const [demoPopoverKind, setDemoPopoverKind] = useState<'edit' | 'plus' | null>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const bindComboRef = useRef<HTMLDivElement>(null);
  const positionAnchorRef = useRef<HTMLDivElement | null>(null);
  if (popoverOpen && cellRef.current) positionAnchorRef.current = cellRef.current;
  const triggerRef = useRef<HTMLDivElement | null>(null);
  triggerRef.current = popoverOpen ? cellRef.current : positionAnchorRef.current;

  const { shouldRender, contentClasses, requestClose: requestOverlayClose, isExiting } = useOverlayAnimation(
    popoverOpen,
    useCallback(() => {
      setPopoverOpen(false);
      setError('');
      setDemoPopoverKind(null);
    }, []),
    true
  );

  usePopoverPositioning({
    triggerRef: triggerRef as React.RefObject<HTMLElement | null>,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1000,
    onAnchorDetached: requestOverlayClose,
    dependencies: [field],
  });

  useClickOutside(popoverRef, requestOverlayClose as (e: MouseEvent) => void, {
    excludeRefs: [bindComboRef],
    excludeSelector: '[data-combo-dropdown]',
    enabled: shouldRender,
    capture: false,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.readers.update>[1]) =>
      api.readers.update(reader.id, hidePrices ? omitPriceKeys(data) : data),
    onSuccess: async (updated: Reader) => {
      showSuccess('Данные сохранены');
      patchReaderInTableCache(queryClient, updated);
      await invalidateReaderCachesExceptTable(queryClient);
      requestOverlayClose();
    },
    onError: (err: Error) => {
      showError(err.message);
      setError(err.message);
    },
  });

  useEffect(() => {
    if (!popoverOpen) return;
    setError('');
    if (field === 'studio') {
      setBindDraft(
        reader.studio?.id
          ? { id: reader.studio.id, name: reader.studio.name }
          : null
      );
    } else if (field === 'legal_entity') {
      setBindDraft(
        reader.legal_entity?.id
          ? { id: reader.legal_entity.id, name: reader.legal_entity.name }
          : null
      );
    } else if (field === 'reader_price') {
      const sorted = sortReaderCatalogPrices(reader.reader_prices);
      setPriceRows(
        sorted.map((p) => ({
          id: p.id,
          price_name: p.price_name?.trim() ?? '',
          price: formatPriceInputDisplay(p.price),
        }))
      );
    } else if (field === 'demo') {
      if (demoPopoverKind === 'plus') return;
      const sorted = sortReaderLinks(reader.links);
      setDemoRows(
        sorted.length
          ? sorted.map((L) => ({ link: L.link, link_name: L.link_name?.trim() ?? '' }))
          : []
      );
    }
  }, [popoverOpen, reader, field, demoPopoverKind]);

  const openEditPopover = useCallback(() => {
    if (disabled) return;
    if (field === 'reader_price' && hidePrices) return;
    if (field === 'demo') setDemoPopoverKind('edit');
    setPopoverOpen(true);
  }, [disabled, field, hidePrices]);

  const openDemoAddFromPlus = useCallback(() => {
    if (disabled || field !== 'demo') return;
    const sorted = sortReaderLinks(reader.links);
    if (sorted.length >= MAX_READER_DEMO_LINKS) return;
    setError('');
    setDemoPopoverKind('plus');
    setDemoRows(
      sorted.length
        ? [
            ...sorted.map((L) => ({
              link: L.link,
              link_name: L.link_name?.trim() ?? '',
            })),
            { link: '', link_name: '' },
          ]
        : [{ link: '', link_name: '' }]
    );
    setPopoverOpen(true);
  }, [disabled, field, reader.links]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setContextMenuPos({ x: e.clientX, y: e.clientY });
      setContextMenuOpen(true);
    },
    [disabled]
  );

  const handleEditFromMenu = useCallback(() => {
    setContextMenuOpen(false);
    openEditPopover();
  }, [openEditPopover]);

  const contextMenuItems = useMemo(() => {
    const items: ContextMenuItem[] = [{ label: 'Редактировать', onClick: handleEditFromMenu }];
    if (cellCommentShell) items.push(cellCommentShell.addCommentMenuItem);
    return items;
  }, [cellCommentShell, handleEditFromMenu]);

  const handleSave = useCallback(() => {
    setError('');
    if (field === 'studio') {
      updateMutation.mutate({ studio_id: bindDraft?.id ?? null });
      return;
    }
    if (field === 'legal_entity') {
      updateMutation.mutate({ legal_entity_id: bindDraft?.id ?? null });
      return;
    }
    if (field === 'reader_price') {
      const payload: Array<{ id?: string; price_name?: string | null; price: number; display_order: number }> = [];
      let outIdx = 0;
      for (const row of priceRows) {
        const name = row.price_name.trim();
        const t = row.price.trim();
        if (!name && !t) continue;
        if (name && !t) {
          setError('Укажите сумму для каждой названной позиции');
          return;
        }
        const n = parseFloat(t.replace(',', '.'));
        if (!Number.isFinite(n) || n < 0) {
          setError('Введите корректную сумму');
          return;
        }
        payload.push({
          id: row.id,
          price_name: name ? name : null,
          price: n,
          display_order: outIdx,
        });
        outIdx += 1;
      }
      if (payload.length > MAX_READER_CATALOG_PRICE_POSITIONS) {
        setError(`Не больше ${MAX_READER_CATALOG_PRICE_POSITIONS} позиций`);
        return;
      }
      updateMutation.mutate({ prices: payload });
      return;
    }
    if (field === 'demo') {
      const links = demoRows
        .map((row, i) => ({
          link: row.link.trim(),
          link_name: row.link_name.trim() ? row.link_name.trim() : null,
          display_order: i,
        }))
        .filter((row) => row.link.length > 0);
      updateMutation.mutate({ links });
    }
  }, [field, bindDraft?.id, priceRows, demoRows, updateMutation]);

  const isPending = updateMutation.isPending;
  const popoverWidth = fieldPopoverWidth(field);
  const hasAnchor = !!triggerRef.current;
  const popoverAnchorStyle: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: isExiting ? 'none' : 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const popoverPanelStyle: React.CSSProperties = {
    width: popoverWidth,
    minWidth: popoverWidth,
    maxWidth: 'calc(100vw - 16px)',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  const demoSlotsDisplay = useMemo(() => demoSlotsFromLinks(reader.links), [reader.links]);

  const formInner = (() => {
    if (field === 'studio' || field === 'legal_entity') {
      return (
        <>
          <ContractorCombo
            value={bindDraft}
            onChange={setBindDraft}
            source={field === 'studio' ? 'studio' : 'legalEntity'}
            placeholder={field === 'studio' ? 'Студия' : 'Юрлицо'}
            containerRef={bindComboRef}
            fullWidth
            disabled={isPending || disabled}
          />
          {error ? <span className="form__error" role="alert">{error}</span> : null}
        </>
      );
    }
    if (field === 'reader_price') {
      return (
        <>
          <ReaderCatalogPricePositionsEditor
            value={priceRows}
            onChange={setPriceRows}
            disabled={isPending || disabled}
            maxRows={MAX_READER_CATALOG_PRICE_POSITIONS}
          />
          {error ? <span className="form__error" role="alert">{error}</span> : null}
        </>
      );
    }
    return (
      <>
        <div className="create-person-modal__reader-links" aria-label="Демо-ссылки чтеца">
          {demoRows.map((item, index) => (
            <div key={index} className="create-person-modal__reader-link-row">
              <Input
                inputClassName="create-person-modal__reader-link-name"
                type="text"
                value={item.link_name}
                onChange={(e) => {
                  const value = e.target.value;
                  setDemoRows((prev) => prev.map((row, i) => (i === index ? { ...row, link_name: value } : row)));
                }}
                disabled={isPending || disabled}
                placeholder="Название"
                autoComplete="off"
                fullWidth
              />
              <div
                className={`create-person-modal__reader-link-url-field ${
                  !isPending && !disabled ? 'create-person-modal__reader-link-url-field_has-clear' : ''
                }`.trim()}
              >
                <input
                  className="create-person-modal__reader-link-url-input"
                  type="url"
                  value={item.link}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDemoRows((prev) => prev.map((row, i) => (i === index ? { ...row, link: value } : row)));
                  }}
                  disabled={isPending || disabled}
                  placeholder="Ссылка на демо"
                  autoComplete="off"
                />
                {!isPending && !disabled ? (
                  <ComboClearButton
                    aria-label="Удалить демо-ссылку"
                    onClick={() => setDemoRows((prev) => prev.filter((_, i) => i !== index))}
                  />
                ) : null}
              </div>
            </div>
          ))}
          <Button
            type="button"
            size="m"
            variant="primary"
            fullWidth
            className="create-person-modal__reader-link-add"
            title={
              demoRows.length >= MAX_READER_DEMO_LINKS
                ? `Не больше ${MAX_READER_DEMO_LINKS} демо-ссылок у чтеца`
                : undefined
            }
            onClick={() =>
              setDemoRows((prev) =>
                prev.length >= MAX_READER_DEMO_LINKS ? prev : [...prev, { link: '', link_name: '' }]
              )
            }
            disabled={isPending || disabled || demoRows.length >= MAX_READER_DEMO_LINKS}
          >
            Добавить ссылку на демо
          </Button>
        </div>
        {error ? <span className="form__error" role="alert">{error}</span> : null}
      </>
    );
  })();

  return (
    <>
      <div
        ref={cellRef}
        className={clsx('editable-cell', disabled && 'readers-table-catalog__editable-cell_disabled')}
        onContextMenu={handleContextMenu}
      >
        <div className="editable-cell__content">
          {field === 'demo' ? (
            <div className="readers-table-catalog__demo-slots" aria-label="Ссылки на демо">
              {demoSlotsDisplay.map((link, idx) => (
                <span key={link?.id ?? `demo-slot-${idx}`} className="readers-table-catalog__demo-slot-wrap">
                  <button
                    type="button"
                    disabled={disabled}
                    className={clsx(
                      'projects-table__action-btn',
                      'readers-table-catalog__demo-slot-btn',
                      link ? 'projects-table__action-btn_has-content readers-table-catalog__demo-slot-btn_has-link' : 'readers-table-catalog__demo-slot-btn_empty'
                    )}
                    title={
                      link
                        ? (link.link_name?.trim() || link.link)
                        : 'Добавить демо-ссылку'
                    }
                    aria-label={
                      link
                        ? `Открыть демо в новой вкладке: ${link.link_name?.trim() || link.link}`
                        : 'Добавить демо-ссылку'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (disabled) return;
                      if (link) {
                        openExternalDemoUrl(link.link);
                      } else {
                        openDemoAddFromPlus();
                      }
                    }}
                  >
                    {link ? (
                      <i className="fa-solid fa-headphones" aria-hidden />
                    ) : (
                      <i className="fa-solid fa-plus" aria-hidden />
                    )}
                  </button>
                </span>
              ))}
            </div>
          ) : (
            children
          )}
        </div>
        <ContextMenu
          isOpen={contextMenuOpen}
          onClose={() => setContextMenuOpen(false)}
          anchorPosition={contextMenuPos}
          items={contextMenuItems}
        />
      </div>
      {shouldRender &&
        createPortal(
          <div ref={popoverRef} style={popoverAnchorStyle}>
            <OverlayIntrinsicSize>
              <div
                style={popoverPanelStyle}
                className={clsx('editable-cell-popover', contentClasses)}
              >
                <EditableCellPopoverHeader onClose={requestOverlayClose}>{fieldTitle(field)}</EditableCellPopoverHeader>
                <OverlayScrollArea className="editable-cell-popover__form editable-cell-popover__form_has-overlay-scroll">
                  <div className="editable-cell-popover__form-stack">{formInner}</div>
                </OverlayScrollArea>
                <div className="editable-cell-popover__footer">
                  <Button type="button" variant="primary" onClick={handleSave} disabled={isPending || disabled}>
                    {isPending ? 'Сохранение…' : 'Сохранить'}
                  </Button>
                </div>
              </div>
            </OverlayIntrinsicSize>
          </div>,
          document.body
        )}
    </>
  );
}
